<script setup lang="ts">import { shallowRef as _shallowRef } from 'vue';

import { prefersReducedMotion } from '@consumer/helpers/screen'
import { Rive as RiveInstance, RiveFile, Layout, Fit } from '@rive-app/webgl2'
import Rive from '@shared/components/Rive/Rive.vue'
import { isTest } from '@shared/environment'

withDefaults(defineProps<{
  riveUrl: string
  riveFile?: RiveFile
  loadingIndicator?: boolean
  loadingText?: string
  layout?: Layout
  stateMachine: string
  artboard: string
  autoBind?: boolean
  fallbackImageUrl?: string
  href?: string
}>(), { layout: () => (new Layout({ fit: Fit.Layout })), })

const emit = defineEmits<{ loaded: [RiveInstance, RiveFile]; beforePlay: [] }>()
let riveInstance = _shallowRef<RiveInstance | null>(null)
const stateMachines = [__props.stateMachine]
const isSSR = import.meta.env.SSR

function onRiveLoaded (r: RiveInstance, f: RiveFile) {
  riveInstance.value = r
  emit('loaded', r, f)
}

function restart () {
  riveInstance.value?.reset({
    artboard: __props.artboard,
    stateMachines,
    autoplay: true,
    autoBind: __props.autoBind,
  })
  emit('beforePlay')
  riveInstance.value?.play()
}

function pause () {
  if (riveInstance.value) riveInstance.value.pause()
}

function play () {
  if (riveInstance.value) {
    emit('beforePlay')
    riveInstance.value.play()
  }
}

defineExpose({
  play,
  pause,
  restart,
})
</script>

<template>
  <div v-if="isSSR || isTest" class="relative w-full h-full">
    <a v-if="href" :href="href" class="absolute w-full h-full">
      <slot name="ssr"/>
    </a>
    <slot v-else name="ssr"/>
  </div>
  <div v-else-if="prefersReducedMotion()" class="relative w-full h-full">
    <a v-if="href" :href="href" class="absolute w-full h-full">
      <slot name="fallback"/>
    </a>
    <slot v-else name="fallback"/>
  </div>
  <Rive
    v-else
    :riveFile="riveFile"
    :src="riveUrl"
    autoplay
    :autoBind="autoBind"
    :stateMachines="stateMachines"
    :artboard="artboard"
    :loadingText="loadingText"
    :layout="layout"
    class="w-full h-full"
    :loadingIndicator="loadingIndicator"
    :href="href"
    @loaded="onRiveLoaded"
  >
    <template v-if="fallbackImageUrl || $slots.loading" #loading="{ loading }">
      <template v-if="loading">
        <img
          v-if="fallbackImageUrl"
          :src="fallbackImageUrl"
          alt="Rive Animation Fallback"
          class="w-full h-full object-contain"
        >
        <slot v-else name="loading"/>
      </template>
    </template>
    <template v-if="fallbackImageUrl || $slots.loadFailed || $slots.fallback" #loadFailed="{ loadFailed }">
      <template v-if="loadFailed">
        <img
          v-if="fallbackImageUrl"
          :src="fallbackImageUrl"
          alt="Rive Animation Fallback"
          class="w-full h-full object-contain"
        >
        <slot v-else name="loadFailed"/>
        <slot v-if="!$slots.loadFailed && !fallbackImageUrl" name="fallback"/>
      </template>
    </template>
  </Rive>
</template>
